/**
 * transitComms.js — TRANSIT-COMMS-INTELLIGENCE-1
 *
 * Zod schemas for:
 *   comms_threads       — per-party communications thread per load
 *   load_geofence_state — geofence ring crossing state per load
 *   load_comms_cadence  — communication cadence tier per load
 *
 * All Firestore access via DataService. EventBus via EventBusService.
 * No direct Firebase imports in this file.
 */
import { z } from 'zod';
import { TenantIdSchema } from './_primitives.js';

// ── Enums ─────────────────────────────────────────────────────────────────────

export const PARTY_TYPE = Object.freeze({
  CUSTOMER:  'customer',
  CARRIER:   'carrier',
  CONSIGNEE: 'consignee',
  BROKER:    'broker',
  AEGIS:     'aegis',
});

export const PartyTypeEnum = z.enum(Object.values(PARTY_TYPE));

export const COMMS_CHANNEL = Object.freeze({
  SMS:    'sms',
  EMAIL:  'email',
  VOICE:  'voice',
  IN_APP: 'in_app',
});

export const CommsChannelEnum = z.enum(Object.values(COMMS_CHANNEL));

export const COMMS_DIRECTION = Object.freeze({
  INBOUND:  'inbound',
  OUTBOUND: 'outbound',
});

export const CommsDirectionEnum = z.enum(Object.values(COMMS_DIRECTION));

export const CADENCE_TIER = Object.freeze({
  STANDARD:              'standard',
  APPOINTMENT_SENSITIVE: 'appointment_sensitive',
  HIGH_VALUE:            'high_value',
  VIP:                   'vip',
  REEFER:                'reefer',
});

export const CadenceTierEnum = z.enum(Object.values(CADENCE_TIER));

export const TIER_SET_BY = Object.freeze({
  AUTO:       'auto',
  DISPATCHER: 'dispatcher',
});

export const TierSetByEnum = z.enum(Object.values(TIER_SET_BY));

// ── Sub-schemas ───────────────────────────────────────────────────────────────

export const CommsEventSchema = z.object({
  id:             z.string(),
  direction:      CommsDirectionEnum,
  channel:        CommsChannelEnum,
  body:           z.string(),
  bodyParsed:     z.record(z.string(), z.any()).nullable().optional(),
  sentAt:         z.string(),                        // ISO timestamp
  deliveredAt:    z.string().nullable().optional(),
  agentGenerated: z.boolean(),
});

const GeofenceRingStateSchema = z.object({
  crossed:          z.boolean(),
  crossedAt:        z.string().nullable().optional(),
  notificationSent: z.boolean(),
});

const DwellRingStateSchema = z.object({
  triggered:          z.boolean(),
  triggeredAt:        z.string().nullable().optional(),
  detentionAlertSent: z.boolean(),
});

const RingConfigSchema = z.object({
  ring1_miles:         z.number().default(150),
  ring2_miles:         z.number().default(30),
  ring4_dwell_minutes: z.number().default(15),
});

// ── Collection schemas ────────────────────────────────────────────────────────

export const CommsThreadSchema = z.object({
  tenantId:   TenantIdSchema,
  loadId:     z.string(),
  partyType:  PartyTypeEnum,
  partyName:  z.string(),
  partyPhone: z.string().nullable().optional(),
  partyEmail: z.string().nullable().optional(),
  events:     z.array(CommsEventSchema).default([]),
  createdAt:  z.string().optional(),
  updatedAt:  z.string().optional(),
  isDeleted:  z.boolean().optional(),
}).passthrough();

export const LoadGeofenceStateSchema = z.object({
  tenantId: TenantIdSchema,
  loadId:   z.string(),
  rings: z.object({
    ring1_150mi:        GeofenceRingStateSchema,
    ring2_30mi:         GeofenceRingStateSchema,
    ring3_delivery_zip: GeofenceRingStateSchema,
    ring4_dwell:        DwellRingStateSchema,
  }),
  ringConfig:  RingConfigSchema.optional(),
  lastGpsLat:  z.number().nullable().optional(),
  lastGpsLon:  z.number().nullable().optional(),
  lastGpsAt:   z.string().nullable().optional(),
  updatedAt:   z.string().optional(),
  isDeleted:   z.boolean().optional(),
}).passthrough();

export const LoadCommsCadenceSchema = z.object({
  tenantId:                TenantIdSchema,
  loadId:                  z.string(),
  tier:                    CadenceTierEnum,
  tierSetBy:               TierSetByEnum,
  tierSetAt:               z.string().optional(),
  rationale:               z.string().nullable().optional(),
  rescheduleOfferSent:     z.boolean().default(false),
  rescheduleOfferAccepted: z.boolean().nullable().optional(),
  rescheduleCompletedAt:   z.string().nullable().optional(),
  isDeleted:               z.boolean().optional(),
}).passthrough();
